import moment from "moment";
import React, { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  Paper,
  Container,
  Button,
  Chip,
  TextField,
  Box,
  Typography,
  Skeleton
} from "@mui/material";
import WebStoriesIcon from "@mui/icons-material/WebStories";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Moment } from "moment";

import conf from "config-dlm";
import Refresh from "components/utils/Refresh";
import ExportCSV from "components/user/ExportCSV";
import LastUpdated from "components/utils/LastUpdated";
import SectionHeader from "components/utils/SectionHeader";
import { usePavmLogs } from "hooks/pavm/logs";
import { ProjectConfigSchema } from "hooks/pavm/base";
import { List, RowComponentProps, useListRef } from "react-window";

type LimitSchema = 100 | undefined;

const estimateHeight = (text: string) => {
  const charsPerLine = 80;
  const lineHeight = 20;
  return Math.ceil(text.length / charsPerLine) * lineHeight + 16;
};


const LogsTable = ({
  name,
  open,
  config,
  onClose,
}: {
  name: string;
  open: boolean;
  config: ProjectConfigSchema;
  onClose: () => void;
}) => {
  const listRef = useListRef(null);
  const [from, setFrom] = useState<Moment>(moment().subtract(1, "hour"));
  const [to, setTo] = useState<Moment>(moment());

  const [limit, setLimit] = useState<LimitSchema>(undefined);
  const [stream, setStream] = useState<boolean>(false);    

  // hook to manually refresh logs without re-firing entire /host/live query
  const manual = usePavmLogs(config.host, name, stream, {
    gte: from.toISOString(),
    lt: to.toISOString()
  }, limit);

  // determine most recent updatedAt time
  const lastUpdatedTime = manual ? manual.dataUpdatedAt : undefined;

  // determine if the logs are truncated
  const lastIsTruncated = manual ? manual.data?.isTruncated : false;

  const rows = manual?.data?.data || [];

  const handleStreamToggle = () => {
    if(limit) {
      setLimit(undefined);
    } else {
      setLimit(100);
    }
    setStream(!stream);
  };

  // When the modal opens, refetch (unless streaming)
  useEffect(() => {
    if (open && !stream) manual.refetch();
  }, [open]);

  useEffect(() => {
    if(open && !stream && rows.length > 0) {
      const list = listRef.current;
      list?.scrollToRow({
        behavior: "smooth", 
        index: rows.length - 1,
        align: "center"
      });
    }
  }, [listRef?.current, lastUpdatedTime, open]);


  // note: end stream if user closes logs window
  const handleClose = () => {
    onClose();
    setStream(false);
  };

  function RowComponent({
    index,
    logs,
    style
  }: RowComponentProps<{
    logs: string[];
  }>) {    
    const log = logs[index];

    return (
      <Box
        style={style}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >      
        {log}
      </Box>
    );
  };

  const DateFrom = () => (
    <TextField
      id="date-filter-gte"
      size="small"
      label="From"
      type="datetime-local"
      value={from.format("YYYY-MM-DDTHH:mm")}
      onChange={(e) => {
        const newFrom = e.target.value;
        // prevent from > to
        if (moment(newFrom).isAfter(to)) return;
        setFrom(moment(newFrom));
      }}
      InputLabelProps={{ shrink: true }}
      inputProps={{
        min: moment().subtract(1, "month").format("YYYY-MM-DDTHH:mm"),
        max: to.format("YYYY-MM-DDTHH:mm"),
      }}
      sx={{ mr: 2 }}
    />
  );

  const DateTo = () => (
    <TextField
      id="date-filter-lt"
      size="small"
      label="To"
      type="datetime-local"
      value={to.format("YYYY-MM-DDTHH:mm")}
      onChange={(e) => {
        const newTo = e.target.value;
        // prevent to < from
        if (moment(newTo).isBefore(from)) return;
        setTo(moment(newTo));
      }}
      inputProps={{
        min: from.format("YYYY-MM-DDTHH:mm"), 
        max: moment().format("YYYY-MM-DDTHH:mm"),
      }}
      InputLabelProps={{ shrink: true }}
    />
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          title={`${name.toUpperCase()} (${config.host})`}
          icon={<WebStoriesIcon />}
          buttons={[
            stream ? (
              <Chip
                key={"logs-stream-chip"}
                label="Live"
                icon={<FiberManualRecordIcon color="error" fontSize="small" />}
                sx={{ mr: 1 }}
              />
            ) : (
              <React.Fragment key={"logs-refresh-and-limit-select"}>
                {lastUpdatedTime && <LastUpdated lastUpdatedTime={moment(lastUpdatedTime)} />}
                <Refresh
                  key={`${config.host}-${name}-refresh`}
                  onClick={manual.refetch}
                  isFetching={manual.isFetching}
                />        
                <DateFrom key={"history-date-from"} />
                <DateTo key={"history-date-to"} />
              </React.Fragment>
            ),
            <Button
              key={"logs-stream-toggle"}
              color={stream ? "error" : "secondary"}
              variant="contained"
              onClick={handleStreamToggle}
              sx={{ mr: 1 }}
            >
              {stream ? <i>End Stream</i> : "Stream"}
            </Button>,
            <ExportCSV
              key={"logs-export-csv"}
              data={manual?.data?.data}
              headers={["Date/Log"]}
              disabled={stream || !manual?.data?.data}
              fileName={`${config.host}-${name}-logs`}
            />,
          ]}
        />

        <DialogContent>
          {lastIsTruncated && <Typography color="error"> Logs are currently truncated. Shrink the date range to avoid truncation</Typography>}
          {lastUpdatedTime ? 
            <Box component={Paper} sx={{ height: 530 }}>
              <List
                listRef={listRef}
                rowCount={rows.length}
                rowHeight={(index) => estimateHeight(rows[index])}
                rowComponent={RowComponent}
                rowProps={{ logs: rows }}
                style={{ height: "100%", width: "100%" }}
              />
            </Box>
            :
            <Skeleton
              variant="rectangular"
              height={530}
              component={Paper}
            />
          }
        </DialogContent>
      </Container>
    </Dialog>
  );
};

export default LogsTable;
